import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { greenBeanService } from '../../services/greenBeanService';
import { inventoryService } from '../../services/inventoryService';
import type { GreenBeanProfile, CreateGreenBeanProfileDTO } from '../../types/greenBean';
import { useLanguage } from '../../features/language/useLanguage';
import { getErrorMessage } from '../../utils/error';

const PROCESS_TYPES = ['Washed', 'Honey', 'Natural', 'Other'] as const;

const toKnownProcessType = (value?: string): (typeof PROCESS_TYPES)[number] | '' => {
  if (!value) return '';
  const normalized = value.trim().toLowerCase();
  const matched = PROCESS_TYPES.find((type) => type.toLowerCase() === normalized);
  return matched ?? '';
};

const createBaseFormValues = (): Partial<GreenBeanProfileFormValues> => ({
  profile_code: '',
  profile_name: '',
  moisture_content: 80,
  harvest_date: '',
  aroma: [],
  sample_physical_check: null,
  cupping_simplified: {
    flavor_notes: [],
  },
  cupping_full: {
    flavor_notes: [],
    mouthfeel_notes: [],
  },
  defect_notes: [],
  _enable_country: false,
  _enable_province: false,
  _enable_region: false,
  _enable_farm: false,
  _enable_producer: false,
  _enable_species: false,
  _enable_variety: false,
  _enable_harvest_date: false,
  _enable_processor: false,
  _enable_wet_mill: false,
  _enable_dry_mill: false,
  _enable_processing_other: false,
  _enable_process_type: false,
  species: '',
});

export type GreenBeanProfileFormValues = Omit<CreateGreenBeanProfileDTO, 'process_type'> & {
  process_type: CreateGreenBeanProfileDTO['process_type'] | '';
  process_type_other?: string;
  cupping_simplified?: {
    fragrance_aroma?: number;
    flavor?: number;
    aftertaste?: number;
    acidity?: number;
    sweetness?: number;
    mouthfeel?: number;
    balance?: number;
    overall?: number;
    flavor_notes?: string[];
  };
  cupping_full?: {
    fragrance_aroma?: number;
    flavor?: number;
    aftertaste?: number;
    acidity?: number;
    sweetness?: number;
    mouthfeel?: number;
    flavor_notes?: string[];
    mouthfeel_notes?: string[];
  };
  defect_notes?: string[];
  _enable_country?: boolean;
  _enable_province?: boolean;
  _enable_region?: boolean;
  _enable_farm?: boolean;
  _enable_producer?: boolean;
  _enable_species?: boolean;
  _enable_variety?: boolean;
  _enable_harvest_date?: boolean;
  _enable_processor?: boolean;
  _enable_wet_mill?: boolean;
  _enable_dry_mill?: boolean;
  _enable_processing_other?: boolean;
  _enable_process_type?: boolean;
};

export type GreenBeanProfileEnableFlags = {
  enableCountry: boolean;
  enableProvince: boolean;
  enableRegion: boolean;
  enableFarm: boolean;
  enableProducer: boolean;
  enableSpecies: boolean;
  enableVariety: boolean;
  enableHarvestDate: boolean;
  enableProcessor: boolean;
  enableWetMill: boolean;
  enableDryMill: boolean;
  enableProcessingOther: boolean;
  enableProcessType: boolean;
};

interface UseGreenBeanProfileFormParams {
  initialData?: Partial<GreenBeanProfile>;
  hideLotSection?: boolean;
  entityType?: 'item' | 'lot' | 'sample' | 'ItemType' | 'LotType' | 'SampleType';
  entityId?: string;
  onSuccess?: () => void;
}

export const useGreenBeanProfileForm = ({
  initialData,
  hideLotSection,
  entityType,
  entityId,
  onSuccess,
}: UseGreenBeanProfileFormParams) => {
  const { t } = useLanguage();
  const { id: routeId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  // When embedded (ProfileManager passes entityId), the route param belongs to
  // the host page (e.g. the lot id on /inventory/lots/:id), not to a green bean
  // profile. Using it would PUT /green-beans/<lotId> and 404.
  const id = entityId ? undefined : routeId;
  const isEditMode = !!id || !!initialData?.id;
  const [itemProfilePromptHandled, setItemProfilePromptHandled] = useState(false);
  const [canShowPrompt, setCanShowPrompt] = useState(false);

  useEffect(() => {
    // Delay showing the prompt to allow the parent modal to finish its transition
    // and establish its focus trap. This prevents focus trap conflicts when
    // the item profile template is cached and available immediately.
    const timer = setTimeout(() => {
      setCanShowPrompt(true);
    }, 400);
    return () => clearTimeout(timer);
  }, []);

  const { control, register, handleSubmit, reset, setValue, formState } = useForm<GreenBeanProfileFormValues>({
    defaultValues: {
      ...createBaseFormValues(),
      ...initialData,
    },
  });

  const { data: allProfiles = [] } = useQuery({
    queryKey: ['green-beans'],
    queryFn: greenBeanService.getAll,
    staleTime: 5 * 60 * 1000,
  });

  const uniqueOptions = useMemo(() => {
    const extract = (key: keyof CreateGreenBeanProfileDTO) =>
      Array.from(new Set(allProfiles.map((p) => p[key] as string).filter(Boolean))).sort();

    return {
      provinces: extract('province'),
      regions: extract('region'),
      farms: extract('farm_name'),
      producers: extract('producer'),
      species: extract('species'),
      varieties: extract('variety'),
      processors: extract('processor'),
    };
  }, [allProfiles]);

  const { data: profile, isLoading: isLoadingProfile } = useQuery({
    queryKey: ['green-beans', id],
    queryFn: () => greenBeanService.getById(id!),
    enabled: !!id && !initialData,
  });

  const itemInfo = (profile ?? initialData) as Partial<GreenBeanProfile> | undefined;
  const lotIdDisplay = profile?.lot_id || initialData?.lot_id;
  const isLotEntity = entityType === 'lot' || entityType === 'LotType';
  const resolvedLotId = isLotEntity && entityId ? entityId : lotIdDisplay;
  const isLotCreateFlow = isLotEntity && !!entityId && !isEditMode;

  const { data: lotInfo } = useQuery({
    queryKey: ['inventory-lot', resolvedLotId],
    queryFn: () => inventoryService.getLotById(resolvedLotId!),
    enabled: !!resolvedLotId,
    staleTime: 5 * 60 * 1000,
  });

  const lotItemId = lotInfo?.inventoryItemId;

  const { data: itemProfileTemplate } = useQuery({
    queryKey: ['green-beans', 'by-item', lotItemId],
    queryFn: () => greenBeanService.getProfileByEntity('ItemType', lotItemId!),
    enabled: isLotCreateFlow && !!lotItemId,
    staleTime: 5 * 60 * 1000,
  });

  const processType = useWatch({ control, name: 'process_type' });
  const userChangedProcessType = useRef(false);

  useEffect(() => {
    if (!userChangedProcessType.current) return;
    if (processType === 'Other') {
      setTimeout(() => {
        document.querySelector<HTMLInputElement>('input[name="process_type_other"]')?.focus();
      }, 0);
    }
  }, [processType]);

  useEffect(() => {
    if (processType !== 'Other') {
      setValue('process_type_other', '');
    }
  }, [processType, setValue]);

  const [
    enableFarm,
    enableProducer,
    enableVariety,
    enableHarvestDate,
    enableProcessor,
    enableWetMill,
    enableDryMill,
    enableProcessingOther,
  ] = useWatch({
    control,
    name: [
      '_enable_farm',
      '_enable_producer',
      '_enable_variety',
      '_enable_harvest_date',
      '_enable_processor',
      '_enable_wet_mill',
      '_enable_dry_mill',
      '_enable_processing_other',
    ],
  });

  const [country, province] = useWatch({
    control,
    name: ['country', 'province'],
  });

  const enableCountry = true;
  const enableProvince = !!country;
  const enableRegion = !!province;
  const enableSpecies = true;
  const enableProcessType = true;

  useEffect(() => {
    if (!country) {
      setValue('province', '');
      setValue('region', '');
    }
  }, [country, setValue]);
  useEffect(() => {
    if (!province) {
      setValue('region', '');
    }
  }, [province, setValue]);
  useEffect(() => {
    if (!enableFarm) setValue('farm_name', '');
  }, [enableFarm, setValue]);
  useEffect(() => {
    if (!enableProducer) setValue('producer', '');
  }, [enableProducer, setValue]);
  useEffect(() => {
    if (!enableVariety) setValue('variety', '');
  }, [enableVariety, setValue]);
  useEffect(() => {
    if (!enableHarvestDate) setValue('harvest_date', '');
  }, [enableHarvestDate, setValue]);
  useEffect(() => {
    if (!enableProcessor) {
      setValue('processor', '');
      setValue('_enable_wet_mill', false);
      setValue('_enable_dry_mill', false);
      setValue('_enable_processing_other', false);
    }
  }, [enableProcessor, setValue]);
  useEffect(() => {
    if (!enableWetMill) setValue('wet_mill', '');
  }, [enableWetMill, setValue]);
  useEffect(() => {
    if (!enableDryMill) setValue('dry_mill', '');
  }, [enableDryMill, setValue]);
  useEffect(() => {
    if (!enableProcessingOther) setValue('processing_other', '');
  }, [enableProcessingOther, setValue]);

  const resetWithDefaults = useCallback((defaultsInput: Partial<GreenBeanProfileFormValues>) => {
    const defaults = { ...defaultsInput };

    if ((entityType === 'item' || entityType === 'ItemType') && entityId) {
      defaults.inventory_item_id = entityId;
      defaults.type = 'ItemType';
    } else if ((entityType === 'lot' || entityType === 'LotType') && entityId) {
      defaults.lot_id = entityId;
      defaults.type = 'LotType';
    } else if ((entityType === 'sample' || entityType === 'SampleType') && entityId) {
      defaults.sample_id = entityId;
      defaults.type = 'SampleType';
    }

    if (hideLotSection && (profile?.lot_id || initialData?.lot_id)) {
      defaults.lot_id = profile?.lot_id || initialData?.lot_id;
    }

    const knownProcessType = toKnownProcessType(defaults.process_type);
    if (knownProcessType) {
      defaults.process_type = knownProcessType;
    } else if (defaults.process_type) {
      defaults.process_type_other = defaults.process_type;
      defaults.process_type = 'Other';
    }

    reset({
      ...createBaseFormValues(),
      ...defaults,
      _enable_country: !!defaults.country,
      _enable_province: !!defaults.province,
      _enable_region: !!defaults.region,
      _enable_farm: !!defaults.farm_name,
      _enable_producer: !!defaults.producer,
      _enable_species: !!defaults.species,
      _enable_variety: !!defaults.variety,
      _enable_harvest_date: !!defaults.harvest_date,
      _enable_processor: !!defaults.processor,
      _enable_wet_mill: !!defaults.wet_mill,
      _enable_dry_mill: !!defaults.dry_mill,
      _enable_processing_other: !!defaults.processing_other,
      _enable_process_type: !!defaults.process_type,
    });

    userChangedProcessType.current = false;
  }, [entityType, entityId, hideLotSection, profile?.lot_id, initialData?.lot_id, reset]);

  useEffect(() => {
    let defaults: Partial<GreenBeanProfileFormValues> = { ...initialData };

    if (profile) {
      defaults = { ...profile, ...defaults };
    }

    resetWithDefaults(defaults);
  }, [profile, initialData, resetWithDefaults]);

  const isItemProfilePromptOpen = canShowPrompt && isLotCreateFlow && !!itemProfileTemplate && !itemProfilePromptHandled;

  const applyItemProfilePreset = () => {
    if (itemProfileTemplate) {
      resetWithDefaults({ ...itemProfileTemplate });
    }
    setItemProfilePromptHandled(true);
  };

  const skipItemProfilePreset = () => {
    setItemProfilePromptHandled(true);
  };

  const mutation = useMutation({
    mutationFn: (data: CreateGreenBeanProfileDTO) => {
      const payload: CreateGreenBeanProfileDTO = {
        ...data,
        aroma: Array.isArray(data.aroma) ? data.aroma : [data.aroma],
      };

      if ((entityType === 'sample' || entityType === 'SampleType') && entityId) {
        payload.sample_id = entityId;
        payload.type = 'SampleType';
      } else if ((entityType === 'lot' || entityType === 'LotType') && entityId) {
        payload.lot_id = entityId;
        payload.type = 'LotType';
      } else if ((entityType === 'item' || entityType === 'ItemType') && entityId) {
        payload.inventory_item_id = entityId;
        payload.type = 'ItemType';
      }

      if (isEditMode && (id || initialData?.id)) {
        return greenBeanService.update(id || initialData!.id!, payload);
      }
      return greenBeanService.create(payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['green-beans'] });
      if (onSuccess) {
        onSuccess();
      } else {
        navigate('/purchasing/green-beans');
      }
    },
    onError: (error) => {
      console.error('Failed to save profile:', error);
      // Append the backend's reason so field-reported failures are diagnosable.
      const detail = getErrorMessage(error, '');
      alert(detail ? `${t('greenBeanProfile.errors.saveFailed')}\n\n${detail}` : t('greenBeanProfile.errors.saveFailed'));
    },
  });

  const onSubmit = (data: GreenBeanProfileFormValues) => {
    const { process_type, process_type_other, ...rest } = data;
    const cleanRest = Object.fromEntries(Object.entries(rest).filter(([key]) => !key.startsWith('_')));

    const payload = {
      ...cleanRest,
      process_type:
        process_type === 'Other'
          ? process_type_other || null
          : process_type || null,
      lot_id: rest.lot_id || null,
      roast_id: rest.roast_id || null,
      inventory_item_id: rest.inventory_item_id || null,
      sample_id: rest.sample_id || null,
    };

    mutation.mutate(payload as CreateGreenBeanProfileDTO);
  };

  return {
    control,
    register,
    handleSubmit,
    formState,
    isEditMode,
    isLoadingProfile,
    onSubmit,
    uniqueOptions,
    itemInfo,
    lotInfo,
    lotIdDisplay,
    isItemProfilePromptOpen,
    applyItemProfilePreset,
    skipItemProfilePreset,
    processType,
    enableFlags: {
      enableCountry: !!enableCountry,
      enableProvince: !!enableProvince,
      enableRegion: !!enableRegion,
      enableFarm: !!enableFarm,
      enableProducer: !!enableProducer,
      enableSpecies: !!enableSpecies,
      enableVariety: !!enableVariety,
      enableHarvestDate: !!enableHarvestDate,
      enableProcessor: !!enableProcessor,
      enableWetMill: !!enableWetMill,
      enableDryMill: !!enableDryMill,
      enableProcessingOther: !!enableProcessingOther,
      enableProcessType: !!enableProcessType,
    },
  };
};
